import { useRef } from "react";
import {
  Box,
  IconButton,
  Typography,
  Chip,
  CircularProgress,
  Breadcrumbs,
  Link,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import ERPGrid from "./ERPGrid";

function DrillDownView({
  onBack,
  onNavigate,
  stack,
  title,
  subtitle,
  columns,
  rows,
  totals,
  loading,
  detailRowCap = 2000,
}: {
  onBack: () => void;
  onNavigate?: (index: number) => void;
  stack?: { title: string }[];
  title: string;
  subtitle?: string;
  columns: ColDef[];
  rows: any[];
  totals?: any | null;
  loading?: boolean;
  detailRowCap?: number;
}) {
  const gridRef = useRef<AgGridReact>(null);
  const capped = !loading && rows.length >= detailRowCap;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <IconButton onClick={onBack} aria-label="Back" sx={{ bgcolor: "action.hover" }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {stack && stack.length > 0 && (
            <Breadcrumbs sx={{ mb: 0.25 }} maxItems={4}>
              <Link
                component="button"
                type="button"
                underline="hover"
                color="inherit"
                variant="caption"
                onClick={() => onNavigate?.(-1)}
              >
                Dashboard
              </Link>
              {stack.map((item, index) => {
                const isLast = index === stack.length - 1;
                if (isLast) {
                  return (
                    <Typography key={index} variant="caption" color="text.primary" noWrap>
                      {item.title}
                    </Typography>
                  );
                }
                return (
                  <Link
                    key={index}
                    component="button"
                    type="button"
                    underline="hover"
                    color="inherit"
                    variant="caption"
                    onClick={() => onNavigate?.(index)}
                    noWrap
                  >
                    {item.title}
                  </Link>
                );
              })}
            </Breadcrumbs>
          )}
          <Typography variant="h6" fontWeight={700} noWrap>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" noWrap title={subtitle}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {!loading && (
          <Chip
            size="small"
            label={`${rows.length} record${rows.length === 1 ? "" : "s"}`}
            variant="outlined"
          />
        )}
      </Box>

      {capped && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          Showing the first {detailRowCap.toLocaleString()} rows. Narrow filters or the date range
          if you need a complete list — totals below only cover these rows.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Alert severity="info">No records for this view.</Alert>
      ) : (
        <Box
          sx={{
            "& .drill-link": { cursor: "pointer", fontWeight: 600 },
            "& .drill-link:hover": { textDecoration: "underline", color: "primary.main" },
          }}
        >
          <ERPGrid
            ref={gridRef}
            gridHeight="75vh"
            columnDefs={columns}
            rowData={rows}
            defaultColDef={{ resizable: true, sortable: true, wrapHeaderText: false }}
            pinnedBottomRowData={totals ? [totals] : undefined}
            getRowStyle={(params: any) =>
              params.node?.rowPinned
                ? { fontWeight: "bold", backgroundColor: "var(--ag-header-background-color)" }
                : undefined
            }
            exportToExcel
          />
        </Box>
      )}
    </Box>
  );
}

export default DrillDownView;
